import { apiFetch } from "@core/http";
import type { ReferralAction, ReferralDetail } from "@core/types";
import type { components } from "@core/types/openapi";

export type { ReferralAction, ReferralDetail };

// SPEC-213 (Gestión del Círculo de Miembros): buscar miembro → CRUD de sus
// referidos. Mismo criterio de dependencia que arriba — este módulo llama al
// endpoint de búsqueda de miembros directamente (SPEC-202) en vez de
// importar `../members/api`, para no crear una arista `referrals → members`
// que no existía hasta ahora.
export type MemberSummary = components["schemas"]["MemberSummary"];
export type Referral = components["schemas"]["Referral"];
export type ReferralCircleEntry = components["schemas"]["ReferralCircleEntry"];
export type ReferralInput = components["schemas"]["ReferralInput"];

const membersBase = "/v1/portal/members";
const referralsBase = "/v1/portal/referrals";

// RF-GCM-01: hasta ocho coincidencias más cercanas, nunca el universo
// completo. El recorte es del cliente porque el contrato de
// `GET /v1/portal/members` (SPEC-202) no declara un `limit` — mismo patrón
// que el prototipo (`resultados.slice(0, 8)`, admin.jsx:1795).
export async function searchMembers(query: string): Promise<MemberSummary[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const params = new URLSearchParams({ search: trimmed });
  const results = await apiFetch<MemberSummary[]>(`${membersBase}?${params}`);
  return results.slice(0, 8);
}

// RF-GCM-02: sin `active`, el servidor lista activos e inactivos a la vez.
export const getReferralCircle = (memberId: string) =>
  apiFetch<ReferralCircleEntry[]>(
    `${membersBase}/${encodeURIComponent(memberId)}/referrals`,
  );

export const createReferral = (memberId: string, input: ReferralInput) =>
  apiFetch<Referral>(
    `${membersBase}/${encodeURIComponent(memberId)}/referrals`,
    {
      method: "POST",
      body: input,
    },
  );

export const editReferral = (referralId: string, input: ReferralInput) =>
  apiFetch<Referral>(`${referralsBase}/${encodeURIComponent(referralId)}`, {
    method: "PUT",
    body: input,
  });

// RF-GCM-05/ANNEX-015 §2.2: la confirmación ya la pidió la pantalla con su
// propio diálogo (mensaje fijo de SPEC-213 §6, `CONFIRM_INACTIVATE_REFERRAL`)
// — a diferencia de `inactivateMember`, acá no hay un impacto dinámico que
// el servidor deba calcular antes de confirmar, así que esta llamada
// siempre envía `confirmed: true` y no hay una segunda vuelta por un 409.
export const inactivateReferral = (referralId: string) =>
  apiFetch<Referral>(
    `${referralsBase}/${encodeURIComponent(referralId)}/inactivate`,
    {
      method: "POST",
      body: { confirmed: true },
    },
  );

// RF-GCM-07: revierte la marca sin pedir motivo ni confirmación.
export const activateReferral = (referralId: string) =>
  apiFetch<Referral>(
    `${referralsBase}/${encodeURIComponent(referralId)}/activate`,
    { method: "POST" },
  );

// Mapeo de códigos del alta/edición/inactivación de referido (RF-GCM-03/04/05,
// SPEC-213 §6). Autocontenido por la misma razón que `REFERRAL_ERROR_MESSAGES`
// de abajo: este módulo no importa de otra feature del portal para un mapeo
// de tres códigos.
// PLAN-009 §3: viaja como `codeMessages` a `reportError` (toast, tono
// error) — CircleManagementScreen ya no pinta estos códigos en un banner de
// pantalla.
export const REFERRAL_CRUD_ERROR_MESSAGES: Record<string, string> = {
  NO_REFERRAL_NAME: "El nombre es obligatorio.",
  NO_REFERRAL_CONTACT:
    "Ingresa al menos un dato de contacto del referido: correo o teléfono.",
};

export const getReferralDetail = (referralId: string) =>
  apiFetch<ReferralDetail>(
    `/v1/portal/referrals/${encodeURIComponent(referralId)}/actions`,
  );

export interface RegisterReferralActionInput {
  actionId: string;
  date: string;
  // SPEC-209 §5/ANEXO-014: solo relevante si `actionId` es la acción que
  // cierra el Gran Premio; el servidor la ignora y guarda 1 en las demás.
  quantity?: number;
  reference?: string;
  confirmed?: boolean;
}

export const registerReferralAction = (
  referralId: string,
  input: RegisterReferralActionInput,
  idempotencyKey: string,
) =>
  apiFetch<{
    action?: ReferralAction;
    progress?: components["schemas"]["Progress"];
    pointsAwarded?: number;
  }>(`/v1/portal/referrals/${encodeURIComponent(referralId)}/actions`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });

export interface VoidReferralActionInput {
  adjustmentReasonId: string;
  detail?: string;
}

export const voidReferralAction = (
  actionId: string,
  input: VoidReferralActionInput,
) =>
  apiFetch<{
    progress?: components["schemas"]["Progress"];
    warnings?: components["schemas"]["Warning"][];
  }>(`/v1/portal/referrals/actions/${encodeURIComponent(actionId)}/void`, {
    method: "POST",
    body: input,
  });

// SPEC-209 §8, literal — el módulo `referrals` no importa de `grand-prize`
// (arista unidireccional grand-prize → referrals de esta corrida), así que
// este mapeo puntual se declara acá, autocontenido, en vez de reusar el de
// la otra feature. PLAN-009 §3: `OLD_DATE` es la reconfirmación (aviso, se
// queda en el panel con un booleano explícito, nunca leyendo el texto);
// `PRIZE_DELIVERED` y cualquier código sin mapear son error de servicio →
// toast (`reportError`/`REFERRAL_ERROR_MESSAGES`).
export const REFERRAL_ERROR_MESSAGES: Record<string, string> = {
  PRIZE_DELIVERED:
    "Este conteo tiene un premio entregado. La corrección debe tramitarse como excepción",
};
export const REFERRAL_OLD_DATE_NOTICE =
  "La fecha indicada tiene más de 30 días. ¿Continuar?";

// SPEC-209 §6.2/§8, literal — `PROGRESS_REGRESSED` viaja en `warnings` de
// una respuesta 200 (no es un error): es el comportamiento normal del
// modelo, así que el portal lo muestra sin alarma. Mapeo autocontenido, misma
// razón que `MESSAGE_BY_CODE` de arriba (arista unidireccional
// grand-prize → referrals: este módulo no importa de `grand-prize`).
const WARNING_TEXT_BY_CODE: Record<string, string> = {
  PROGRESS_REGRESSED: "El avance bajó de la meta y el estado volvió a En curso",
};

export function referralWarningText(code: string): string | null {
  return WARNING_TEXT_BY_CODE[code] ?? null;
}
